import { useState, useEffect } from "react";
import { Users2, Plus, Loader2, Trash2, Edit3, Check, X, Shield, Eye, EyeOff, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PERMISSIONS = [
  { id: "send_sms", label: "Send SMS", desc: "Can compose and send messages" },
  { id: "view_reports", label: "View Reports", desc: "Can view analytics and sent messages" },
  { id: "buy_credits", label: "Buy Credits", desc: "Can initiate top-up or subscription purchases" },
  { id: "access_api", label: "Access API", desc: "Can create and view API keys" },
  { id: "view_billing", label: "View Billing", desc: "Can view wallet balance and transactions" },
];

export default function Team() {
  const { user } = useAuth();
  const isOwner = !user?.owner_id;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showPass, setShowPass] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [resetForm, setResetForm] = useState({ password: "", confirm: "" });
  const [showResetPass, setShowResetPass] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", role_name: "Team Member",
    permissions: []
  });

  useEffect(() => {
    if (isOwner) loadMembers();
    else setLoading(false);
  }, [isOwner]);

  const loadMembers = async () => {
    try {
      const res = await API.get("/team");
      setMembers(res.data.members || []);
    } catch { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  };

  const togglePerm = (id) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(id)
        ? f.permissions.filter(p => p !== id)
        : [...f.permissions, id]
    }));
  };

  const createMember = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      await API.post("/team", form);
      toast.success("Team member created");
      setShowCreate(false);
      setForm({ name: "", email: "", password: "", role_name: "Team Member", permissions: [] });
      loadMembers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create member"); }
    finally { setSaving(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await API.delete(`/team/${id}`);
      toast.success("Member removed");
      loadMembers();
    } catch { toast.error("Failed to remove"); }
  };

  const startEdit = (m) => {
    setEditingId(m._id);
    setEditForm({ role_name: m.role_name || "", permissions: m.permissions || [], status: m.status });
  };

  const saveEdit = async (id) => {
    try {
      await API.put(`/team/${id}`, editForm);
      toast.success("Member updated");
      setEditingId(null);
      loadMembers();
    } catch { toast.error("Failed to update"); }
  };

  const resetPassword = async (id) => {
    if (!resetForm.password || resetForm.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (resetForm.password !== resetForm.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await API.post(`/team/${id}/reset-password`, { password: resetForm.password });
      toast.success("Password reset successfully");
      setResettingId(null);
      setResetForm({ password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
  };

  if (!isOwner) return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm max-w-md mx-auto mt-10">
      <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto mb-4">
        <Shield size={24} className="text-slate-400" />
      </div>
      <h2 className="text-lg font-bold text-slate-900">Team Management</h2>
      <p className="text-slate-500 text-sm mt-2">Only the account owner can manage the team. Contact your account administrator for access changes.</p>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={24} className="animate-spin text-[#0c1e41]" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Team Management</h1>
          <p className="text-sm text-slate-500 mt-1">Add staff members and control their access permissions</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="create-member-btn">
          <Plus size={15} className="mr-2" /> Add Member
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">New Team Member</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="h-9 text-sm" data-testid="member-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" className="h-9 text-sm" data-testid="member-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Password *</Label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Set a password" className="h-9 text-sm pr-10" data-testid="member-password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-700">Role Title</Label>
              <Input value={form.role_name} onChange={e => setForm({ ...form, role_name: e.target.value })} placeholder="e.g. Marketing Manager" className="h-9 text-sm" data-testid="member-role-input" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold text-slate-700 mb-2">Permissions</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMISSIONS.map(p => (
                <label key={p.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${form.permissions.includes(p.id) ? "bg-[#eef1f8] border-[#c0cfe8]" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`} data-testid={`perm-${p.id}`}>
                  <input type="checkbox" checked={form.permissions.includes(p.id)} onChange={() => togglePerm(p.id)} className="mt-0.5 accent-[#0c1e41]" />
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{p.label}</p>
                    <p className="text-xs text-slate-500">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={createMember} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] text-sm shadow-sm" data-testid="save-member-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Create Member
            </Button>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="text-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Members list */}
      {members.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <Users2 size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No team members yet</p>
          <p className="text-slate-400 text-sm mt-1">Add your first staff member to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map(m => (
            <div key={m._id} className="space-y-0">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`member-row-${m._id}`}>
              {editingId === m._id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Role Title</Label>
                      <Input value={editForm.role_name} onChange={e => setEditForm({ ...editForm, role_name: e.target.value })} className="h-8 text-xs mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Status</Label>
                      <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full h-8 mt-1 border border-slate-300 rounded-md px-2 text-xs bg-white">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Permissions</p>
                    <div className="flex flex-wrap gap-2">
                      {PERMISSIONS.map(p => (
                        <label key={p.id} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border cursor-pointer transition-all ${editForm.permissions?.includes(p.id) ? "bg-[#eef1f8] border-[#c0cfe8] text-[#0c1e41] font-semibold" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                          <input type="checkbox" checked={editForm.permissions?.includes(p.id)} onChange={() => setEditForm(f => ({ ...f, permissions: f.permissions?.includes(p.id) ? f.permissions.filter(x => x !== p.id) : [...(f.permissions || []), p.id] }))} className="hidden" />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(m._id)} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700" data-testid="save-edit-member-btn"><Check size={12} /> Save</button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300"><X size={12} /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-[#091629] flex items-center justify-center text-white text-sm font-bold shadow-sm shrink-0">
                      {m.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 text-sm">{m.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${m.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{m.status}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{m.email} · {m.role_name || "Team Member"}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(m.permissions || []).map(pid => {
                          const p = PERMISSIONS.find(x => x.id === pid);
                          return p ? (
                            <span key={pid} className="text-xs bg-[#eef1f8] text-[#0c1e41] border border-[#c0cfe8] px-1.5 py-0.5 rounded-full font-medium">{p.label}</span>
                          ) : null;
                        })}
                        {(!m.permissions || m.permissions.length === 0) && (
                          <span className="text-xs text-slate-400">No permissions assigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(m)} className="p-1.5 text-slate-500 hover:text-[#0c1e41] hover:bg-slate-100 rounded-lg" data-testid={`edit-member-${m._id}`}>
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => { setResettingId(m._id); setResetForm({ password: "", confirm: "" }); setShowResetPass(false); }}
                      className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                      title="Reset password"
                      data-testid={`reset-password-${m._id}`}
                    >
                      <KeyRound size={14} />
                    </button>
                    <button onClick={() => deleteMember(m._id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-member-${m._id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            {/* Password reset form */}
            {resettingId === m._id && (
              <div className="mt-3 pt-3 border-t border-amber-200 bg-amber-50 rounded-b-xl -mx-4 -mb-4 px-4 pb-4">
                <p className="text-xs font-semibold text-amber-700 mb-2">Reset Password for {m.name}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    <Input
                      type={showResetPass ? "text" : "password"}
                      placeholder="New password (min 8 chars)"
                      value={resetForm.password}
                      onChange={e => setResetForm({ ...resetForm, password: e.target.value })}
                      className="h-8 text-xs pr-9 bg-white border-amber-200"
                      data-testid={`new-password-input-${m._id}`}
                    />
                    <button type="button" onClick={() => setShowResetPass(!showResetPass)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                      {showResetPass ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                  <Input
                    type={showResetPass ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={resetForm.confirm}
                    onChange={e => setResetForm({ ...resetForm, confirm: e.target.value })}
                    className="h-8 text-xs bg-white border-amber-200"
                    data-testid={`confirm-password-input-${m._id}`}
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => resetPassword(m._id)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium"
                    data-testid={`confirm-reset-password-${m._id}`}
                  >
                    <Check size={12} /> Reset Password
                  </button>
                  <button onClick={() => setResettingId(null)} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-white text-slate-600 rounded-md border border-slate-200 hover:bg-slate-50">
                    <X size={12} /> Cancel
                  </button>
                </div>
              </div>
            )}
            </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
